/**
 * Habit Weekly View — mobile card-based daily habit list.
 *
 * Fixes applied:
 *   - Weekly ga o'tganda hozirgi hafta ochiladi (store fix)
 *   - Growth row h-20 (#001f2e), day headers #131b27, today primary/10
 *   - Habit cards: emoji ring (haftalik %), 3-dot menu, challenge row, details row
 *   - Bar chart: tall bars, fon, bajarilgan/foiz, haftalik progress bar, legend
 */

import React, { useMemo, useCallback, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { format, addDays, isSameDay, startOfWeek } from 'date-fns'
import { uz } from 'date-fns/locale'
import { motion } from 'framer-motion'
import {
  ChevronLeft, ChevronRight, CalendarDays, LayoutList,
  Flame, Clock4, Hourglass, Target, Swords, Check,
  MoreHorizontal, Pencil, Trash2,
} from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useHomeStore } from '@turnifly/shared/stores'
import type { ViewMode } from '@turnifly/shared/stores'
import { useMonthlyHabits, useToggleHabit, useDeleteHabit } from '@turnifly/shared/hooks'
import {
  habitAppliesToDate, formatDateStr, isDateToggleable, buildLogIndex,
} from '@turnifly/shared/utils'
import type { DayStats } from '@turnifly/shared/utils'
import { HabitCompletionFlow, habitNeedsFlow } from './habit-completion-flow'
import type { PendingToggle } from './habit-completion-flow'
import { GrowthIndicator } from './growth-row'
import { Skeleton } from '@turnifly/shared/ui'
import type { MonthlyHabitItem, MonthlyHabitsResponse } from '@turnifly/shared/types'
import { triggerHapticImpact } from '@turnifly/shared/utils'

/* ── Design tokens (oylik bilan bir xil) ── */
const HINT = '#768c9e'
const BG_GROWTH = '#001f2e'
const BG_HEADER = '#131b27'

function rateColorHex(pct: number): string {
  if (pct >= 80) return '#22C55E'
  if (pct >= 60) return '#EAB308'
  if (pct >= 40) return '#F97316'
  if (pct > 0) return '#EF4444'
  return 'transparent'
}
function ringColor(pct: number): string {
  if (pct >= 80) return '#22C55E'
  if (pct >= 60) return '#EAB308'
  if (pct >= 40) return '#F97316'
  if (pct > 0) return '#EF4444'
  return 'rgba(255,255,255,0.06)'
}

const RING_SZ = 38; const RING_SW = 2.5; const RING_R = (RING_SZ - RING_SW) / 2; const RING_C = 2 * Math.PI * RING_R

const VIEW_MODES: { id: ViewMode; icon: React.ElementType }[] = [
  { id: 'monthly', icon: CalendarDays },
  { id: 'weekly', icon: LayoutList },
]

const LEGEND = [
  { min: 0, max: 39, color: '#EF4444', key: 'home.legendBad' },
  { min: 40, max: 59, color: '#F97316', key: 'home.legendAverage' },
  { min: 60, max: 79, color: '#EAB308', key: 'home.legendGood' },
  { min: 80, max: 100, color: '#22C55E', key: 'home.legendGreat' },
] as const

/* ═══ MAIN ═══ */

export const HabitWeeklyView: React.FC = () => {
  const { t, i18n } = useTranslation()
  const userId = useAuthStore((s) => s.user?.id)
  const gender = useAuthStore((s) => s.user?.gender === 'female' ? 'female' : 'male') as 'male' | 'female'
  const store = useHomeStore()
  const { year, month, viewMode, weekStart } = store
  const toggleMutation = useToggleHabit(year, month)
  const locale = i18n.language === 'uz' ? uz : undefined

  const monday = useMemo(() => startOfWeek(new Date(weekStart), { weekStartsOn: 1 }), [weekStart])
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(monday, i)), [monday])
  const weekDateStrs = useMemo(() => weekDays.map((d) => formatDateStr(d.getFullYear(), d.getMonth() + 1, d.getDate())), [weekDays])

  const today = useMemo(() => new Date(), [])
  const todayStr = useMemo(() => formatDateStr(today.getFullYear(), today.getMonth() + 1, today.getDate()), [today])

  const { data, isLoading } = useMonthlyHabits(userId, year, month)

  const habits = useMemo((): MonthlyHabitItem[] => {
    if (!data) return []
    const raw = (data as MonthlyHabitsResponse).habits ?? []
    return [...raw].sort((a, b) => {
      const at = a.time_start, bt = b.time_start
      if (at && bt) return at.localeCompare(bt)
      if (at && !bt) return -1
      if (!at && bt) return 1
      return (a.sort_order ?? 0) - (b.sort_order ?? 0)
    })
  }, [data])

  const logIndex = useMemo(() => buildLogIndex(habits), [habits])

  const { incompleteHabits, completedHabits } = useMemo(() => {
    const inc: MonthlyHabitItem[] = [], done: MonthlyHabitItem[] = []
    for (const h of habits) {
      if (!habitAppliesToDate(h, todayStr)) continue
      if (h.logs.some((l) => l.log_date === todayStr && l.completed)) done.push(h); else inc.push(h)
    }
    return { incompleteHabits: inc, completedHabits: done }
  }, [habits, todayStr])

  const weeklyRates = useMemo(() => {
    const map = new Map<string, number>()
    for (const h of habits) {
      let app = 0, done = 0
      for (const ds of weekDateStrs) {
        if (habitAppliesToDate(h, ds)) { app++; if (h.logs.some((l) => l.log_date === ds && l.completed)) done++ }
      }
      map.set(h.id, app > 0 ? Math.round((done / app) * 100) : 0)
    }
    return map
  }, [habits, weekDateStrs])

  const dayStats = useMemo((): DayStats[] => {
    return weekDateStrs.map((ds) => {
      const set = logIndex.get(ds)
      let app = 0, done = 0
      for (const h of habits) { if (habitAppliesToDate(h, ds)) { app++; if (set?.has(h.id)) done++ } }
      return { completed: done, total: app, rate: app > 0 ? Math.round((done / app) * 100) : 0 }
    })
  }, [weekDateStrs, logIndex, habits])

  // Weekly totals
  const weekTotal = useMemo(() => {
    let app = 0, done = 0
    for (const s of dayStats) { app += s.total; done += s.completed }
    return { total: app, done, rate: app > 0 ? Math.round((done / app) * 100) : 0 }
  }, [dayStats])

  const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null)

  const handleToggle = useCallback((habit: MonthlyHabitItem, completed: boolean): void => {
    if (!userId) return
    if (!completed) { toggleMutation.mutate({ userId, habitId: habit.id, logDate: todayStr, completed: false }); return }
    if (habitNeedsFlow(habit)) { setPendingToggle({ habit, logDate: todayStr }) }
    else { toggleMutation.mutate({ userId, habitId: habit.id, logDate: todayStr, completed: true }) }
  }, [userId, toggleMutation, todayStr])

  const handleFlowComplete = useCallback((habitId: string, logDate: string, fd: { value: number | null; proofUrls: Array<{ url: string; type: 'image' | 'video' }>; proofText: string | null }): void => {
    if (!userId) return
    toggleMutation.mutate({ userId, habitId, logDate, completed: true, value: fd.value, proofUrls: fd.proofUrls, proofText: fd.proofText })
    setPendingToggle(null)
  }, [userId, toggleMutation])

  const weekLabel = useMemo(() => {
    const s = weekDays[0], e = weekDays[6]
    return s.getMonth() === e.getMonth()
      ? `${format(s, 'd')} – ${format(e, 'd MMMM', { locale })}`
      : `${format(s, 'd MMM', { locale })} – ${format(e, 'd MMM', { locale })}`
  }, [weekDays, locale])

  const todayLabel = useMemo(() => format(today, 'd MMMM, EEEE', { locale }), [today, locale])
  const totalToday = incompleteHabits.length + completedHabits.length
  const canToggle = isDateToggleable(todayStr, todayStr)

  if (isLoading) {
    return <div className="rounded-2xl glass p-4 flex flex-col gap-3"><Skeleton className="h-10 w-full rounded-lg" /><Skeleton className="h-20 w-full rounded-lg" /><Skeleton className="h-14 w-full rounded-lg" /></div>
  }

  return (
    <div className="flex flex-col gap-2.5">
      {/* ━━━ Top: header + growth + days ━━━ */}
      <div className="rounded-2xl glass overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-glass-solid border-b border-white/[0.06]">
          <div className="flex items-center gap-1.5">
            <button onClick={store.prevWeek} className="p-1.5 rounded-lg text-text-hint/40 hover:text-text hover:bg-white/[0.04] transition-colors tap-highlight-transparent"><ChevronLeft size={16} /></button>
            <button onClick={store.goToToday} className="font-display font-bold text-[14px] text-text capitalize px-2 py-1 rounded-lg hover:bg-white/[0.04] transition-colors">{weekLabel}</button>
            <button onClick={store.nextWeek} className="p-1.5 rounded-lg text-text-hint/40 hover:text-text hover:bg-white/[0.04] transition-colors tap-highlight-transparent"><ChevronRight size={16} /></button>
          </div>
          <div className="flex items-center gap-0.5">
            {VIEW_MODES.map((m) => { const I = m.icon; const act = viewMode === m.id; return (
              <button key={m.id} onClick={() => store.setViewMode(m.id)} className={`p-2 rounded-lg transition-colors tap-highlight-transparent ${act ? 'text-primary bg-primary/8' : 'text-text-hint/30 hover:text-text-hint/60'}`}><I size={15} /></button>
            ) })}
          </div>
        </div>

        <div className="grid grid-cols-7 border-b border-white/[0.06]" style={{ backgroundColor: BG_GROWTH }}>
          {weekDays.map((day, i) => (
            <div key={`g-${i}`} className="h-20 flex items-center justify-center">
              <GrowthIndicator day={day.getDate()} year={day.getFullYear()} month={day.getMonth() + 1} completionRate={dayStats[i].rate} gender={gender} />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7" style={{ backgroundColor: BG_HEADER }}>
          {weekDays.map((day, i) => {
            const isTd = isSameDay(day, today)
            return (
              <div key={`dh-${i}`} className={`py-2.5 flex flex-col items-center justify-center ${isTd ? 'bg-primary/[0.1]' : ''}`}>
                <span className={`text-[10px] uppercase font-semibold ${isTd ? 'text-primary' : ''}`} style={isTd ? undefined : { color: HINT }}>{format(day, 'EEEEEE', { locale })}</span>
                <span className={`text-[14px] font-display font-bold mt-0.5 ${isTd ? 'text-primary' : 'text-text'}`}>{format(day, 'd')}</span>
              </div>
            )
          })}
        </div>
      </div>

      {/* ━━━ Today label ━━━ */}
      {totalToday > 0 && (
        <div className="flex items-center justify-between px-1 pt-1">
          <span className="text-[12px] font-semibold capitalize" style={{ color: HINT }}>{todayLabel}</span>
          <span className="text-[12px] font-bold tabular-nums" style={{ color: HINT }}>{completedHabits.length}/{totalToday}</span>
        </div>
      )}

      {/* ━━━ Habit cards ━━━ */}
      {incompleteHabits.map((h) => <WeekHabitCard key={h.id} habit={h} isCompleted={false} weeklyRate={weeklyRates.get(h.id) ?? 0} canToggle={canToggle} onToggle={handleToggle} lang={i18n.language} t={t} />)}
      {completedHabits.map((h) => <WeekHabitCard key={h.id} habit={h} isCompleted weeklyRate={weeklyRates.get(h.id) ?? 0} canToggle={canToggle} onToggle={handleToggle} lang={i18n.language} t={t} />)}

      {totalToday === 0 && habits.length > 0 && (
        <div className="rounded-2xl glass p-6 text-center"><span className="text-sm text-text-hint">{t('home.noHabitsToday')}</span></div>
      )}
      {habits.length === 0 && (
        <div className="rounded-2xl glass min-h-[160px] flex flex-col items-center justify-center text-center p-8 gap-3">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary text-2xl">+</div>
          <h4 className="text-text font-display font-semibold text-base">{t('home.noHabits')}</h4>
          <p className="text-sm text-text-hint max-w-[220px]">{t('home.noHabitsDesc')}</p>
        </div>
      )}

      {/* ━━━ Bar chart + weekly progress + legend ━━━ */}
      {habits.length > 0 && (
        <div className="rounded-2xl glass overflow-hidden">
          {/* Bars */}
          <div className="px-4 pt-4 pb-2">
            <div className="grid grid-cols-7 gap-2">
              {dayStats.map((s, i) => {
                const isTd = isSameDay(weekDays[i], today)
                const barH = Math.max(s.rate > 0 ? 6 : 0, Math.round((s.rate / 100) * 72))
                const color = rateColorHex(s.rate)
                return (
                  <div key={`bar-${i}`} className="flex flex-col items-center">
                    <div className={`w-full h-20 flex items-end justify-center rounded-lg overflow-hidden ${isTd ? 'bg-primary/[0.08]' : 'bg-white/[0.02]'}`}>
                      {s.rate > 0 && (
                        <div className="w-full max-w-[24px] rounded-t-[6px] transition-all duration-700 ease-out" style={{ height: `${barH}px`, background: `linear-gradient(to top, ${color}, ${color}BB)`, boxShadow: `0 0 10px ${color}25` }} />
                      )}
                    </div>
                    <span className={`text-[11px] font-bold tabular-nums mt-1.5 ${isTd ? 'text-primary' : ''}`} style={isTd ? undefined : { color: HINT }}>{s.completed}</span>
                    <span className="text-[9px] font-semibold tabular-nums mt-px" style={{ color: s.rate > 0 ? color : `${HINT}40` }}>{s.rate}%</span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Weekly progress bar */}
          <div className="px-4 py-3 border-t border-white/[0.06]">
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-bold tabular-nums shrink-0" style={{ color: HINT }}>{weekTotal.done}/{weekTotal.total}</span>
              <div className="flex-1 h-2 bg-white/[0.04] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${weekTotal.rate}%`, backgroundColor: rateColorHex(weekTotal.rate) }} />
              </div>
              <span className="text-[10px] font-bold tabular-nums shrink-0" style={{ color: rateColorHex(weekTotal.rate) }}>{weekTotal.rate}%</span>
            </div>
          </div>

          {/* Legend */}
          <div className="px-4 py-2.5 border-t border-white/[0.06] flex items-center justify-center gap-4">
            {LEGEND.map((l) => (
              <div key={l.key} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-[3px]" style={{ backgroundColor: l.color, opacity: 0.85 }} />
                <span className="text-[10px] font-semibold" style={{ color: HINT }}>{t(l.key)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <HabitCompletionFlow pending={pendingToggle} onComplete={handleFlowComplete} onCancel={() => setPendingToggle(null)} />
    </div>
  )
}

/* ── Habit Card with ring + menu + details ── */

interface WeekHabitCardProps {
  habit: MonthlyHabitItem
  isCompleted: boolean
  weeklyRate: number
  canToggle: boolean
  onToggle: (habit: MonthlyHabitItem, completed: boolean) => void
  lang: string
  t: (key: string) => string
}

const WeekHabitCard: React.FC<WeekHabitCardProps> = React.memo(({ habit, isCompleted, weeklyRate, canToggle, onToggle, lang, t }) => {
  const streak = habit.streak.current
  const timeStart = habit.time_start ? habit.time_start.slice(0, 5) : null
  const duration = habit.time_duration
  const hasCh = !!habit.challenge_id
  const hasDetails = !!(timeStart || (duration && duration > 0) || (habit.value_required && habit.value_target))
  const color = ringColor(weeklyRate)
  const offset = RING_C * (1 - weeklyRate / 100)

  const setEditingHabit = useHomeStore((s) => s.setEditingHabit)
  const setViewingHabitId = useHomeStore((s) => s.setViewingHabitId)
  const deleteMutation = useDeleteHabit()
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)

  const openMenu = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation()
    if (btnRef.current) { const r = btnRef.current.getBoundingClientRect(); setMenuPos({ top: r.bottom + 4, left: r.right - 140 }) }
    setMenuOpen(true)
  }, [])

  const handleClick = (): void => {
    if (!canToggle) return
    triggerHapticImpact(isCompleted ? 'light' : 'medium')
    onToggle(habit, !isCompleted)
  }

  return (
    <motion.div layout className={`rounded-xl bg-white/[0.03] border border-white/[0.06] flex items-center gap-3 px-3 py-2.5 transition-opacity ${isCompleted ? 'opacity-45' : ''}`}>
      {/* Emoji ring */}
      <div className="shrink-0 relative" style={{ width: RING_SZ, height: RING_SZ }}>
        <svg width={RING_SZ} height={RING_SZ} viewBox={`0 0 ${RING_SZ} ${RING_SZ}`} className="absolute inset-0 -rotate-90">
          <circle cx={RING_SZ / 2} cy={RING_SZ / 2} r={RING_R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={RING_SW} />
          {weeklyRate > 0 && <circle cx={RING_SZ / 2} cy={RING_SZ / 2} r={RING_R} fill="none" stroke={color} strokeWidth={RING_SW} strokeLinecap="round" strokeDasharray={RING_C} strokeDashoffset={offset} className="transition-all duration-700 ease-out" />}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center"><span className="text-[17px] leading-none">{habit.icon}</span></div>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 flex flex-col gap-px">
        {/* Name row */}
        <div className="flex items-center gap-1 min-w-0">
          <button onClick={() => setViewingHabitId(habit.id)} className={`text-[13px] font-display font-semibold truncate flex-1 min-w-0 text-left tap-highlight-transparent hover:text-primary/90 transition-colors ${isCompleted ? 'line-through text-text-hint' : 'text-text'}`}>{habit.name}</button>
          {streak > 0 && <span className="shrink-0 inline-flex items-center gap-px text-[9px] font-bold text-streak"><Flame size={9} fill="currentColor" />{streak}</span>}
          <button ref={btnRef} onClick={openMenu} className="shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-white/[0.06] transition-colors tap-highlight-transparent" style={{ color: HINT }}><MoreHorizontal size={13} /></button>
        </div>

        {/* Challenge row */}
        {hasCh && (
          <div className="flex items-center gap-1 min-w-0">
            <Swords size={10} className="shrink-0" style={{ color: 'var(--color-accent)' }} fill="currentColor" />
            <span className="text-[10px] font-semibold truncate" style={{ color: HINT }}>{t('home.challenge')}</span>
          </div>
        )}

        {/* Details row */}
        {hasDetails && (
          <div className="flex items-center gap-2 min-w-0">
            {timeStart && <span className="inline-flex items-center gap-0.5 text-[10px]" style={{ color: HINT }}><Clock4 size={9} style={{ color: 'var(--color-primary)' }} fill="currentColor" stroke="var(--color-bg)" />{timeStart}</span>}
            {duration != null && duration > 0 && <span className="inline-flex items-center gap-0.5 text-[10px]" style={{ color: HINT }}><Hourglass size={9} style={{ color: 'var(--color-warning)' }} fill="currentColor" />{duration}{lang === 'uz' ? ' daq' : ' min'}</span>}
            {habit.value_required && habit.value_target && <span className="inline-flex items-center gap-0.5 text-[10px]" style={{ color: HINT }}><Target size={9} style={{ color: 'var(--color-success)' }} fill="currentColor" stroke="var(--color-bg)" />{habit.value_target}{habit.value_unit ? ` ${habit.value_unit}` : ''}</span>}
          </div>
        )}
      </div>

      {/* Checkbox */}
      <button onClick={handleClick} disabled={!canToggle} className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 tap-highlight-transparent ${isCompleted ? 'bg-success/20 border border-success/50' : 'border border-white/[0.1] bg-white/[0.03] hover:border-white/[0.2] active:bg-white/[0.06]'} ${!canToggle ? 'cursor-not-allowed opacity-50' : ''}`}>
        {isCompleted && <motion.div initial={{ scale: 0.3 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400, damping: 15 }}><Check size={18} className="text-success" strokeWidth={3} /></motion.div>}
      </button>

      {/* Menu portal */}
      {menuOpen && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setMenuOpen(false)} />
          <div className="fixed z-[9999] bg-surface border border-white/[0.08] rounded-lg overflow-hidden min-w-[140px] shadow-xl" style={{ top: menuPos.top, left: menuPos.left }}>
            <button onClick={() => { setMenuOpen(false); setEditingHabit(habit) }} className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-text hover:bg-white/[0.04] transition-colors"><Pencil size={13} className="text-text-hint/60" />{t('common.edit')}</button>
            <div className="h-px bg-white/[0.06] mx-2" />
            <button onClick={() => { setMenuOpen(false); deleteMutation.mutate(habit.id) }} className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-danger hover:bg-white/[0.04] transition-colors"><Trash2 size={13} />{t('common.delete')}</button>
          </div>
        </>,
        document.body,
      )}
    </motion.div>
  )
})

WeekHabitCard.displayName = 'WeekHabitCard'
